import { Button, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { StickyHeader } from '@hero/atoms';
import { WelcomeModal } from '@hero/components/modals';
import { configs, constants } from '@hero/configs';
import { ProductCard, ProductListItemDetail, ProductSearch } from '@hero/molecules';
import {
  BannerSlider,
  HomeBrandSlider,
  HomePopularCollections,
  HomeTopCategories,
  HomeUpcomingOccasions,
  StaticBanners
} from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetUser, useUpdateUser } from '@hero/services/organization-service/hooks';
import { useSearchProducts } from '@hero/services/product-service/hooks';
import { useShoppingCart } from '@hero/store/hooks';

import { withMerchantComponents } from '../../../utils/merchantUtils';

import * as S from './style';

function HomeTemplate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { products, updateProducts } = useShoppingCart();
  const { data: userData } = useGetUser();
  const { mutateAsync: onUserUpdate } = useUpdateUser({
    invalidate: false
  });
  const [productDetail, setProductDetail] = useState(false);

  const handleHideProductModal = () => {
    setProductDetail(null);
  };

  const { data: productsData } = useSearchProducts({
    params: {
      filters: { categoryIds: [configs?.productList?.defaultFilterId] },
      pagination: { limit: 10 }
    },
    options: {
      enabled: true
    }
  });

  const handleShowProductModal = ({ product, isSelected }) => {
    setProductDetail({
      product,
      isSelected,
      productWithSkuSelection: products.find(p => p.merchantProductId === product.merchantProductId)
    });
  };

  const onSelectProduct = product => {
    const isSelected =
      products.findIndex(item => item.merchantProductId === product.merchantProductId) >= 0;

    if (isSelected) {
      updateProducts(products.filter(item => item.merchantProductId !== product.merchantProductId));
    } else {
      updateProducts([...products, product]);
    }

    handleHideProductModal();
  };

  useEffect(() => {
    if (!configs.homePage.enabled) {
      navigate(ROUTE_PATHS.DASHBOARD, { replace: true });
    }
  }, []);

  const showWelcomeModal =
    !userData?.meta?.isWelcomeModalOpened && configs.homePage.showWelcomeModal;

  useEffect(() => {
    if (showWelcomeModal) {
      onUserUpdate({ meta: { isWelcomeModalOpened: true } });
    }
  }, [showWelcomeModal]);

  return (
    <S.HomeTemplate>
      <BannerSlider />
      {showWelcomeModal && <WelcomeModal />}
      <S.Content>
        <StickyHeader scrollThreshold={0}>
          <ProductSearch
            placeholder="findAGift"
            selectedProducts={products}
            updateProducts={updateProducts}
            inputShown={true}
          />
        </StickyHeader>
        <S.ProductSearchContainer />
        <HomeTopCategories />
        <HomePopularCollections />
        <StaticBanners />
        <HomeUpcomingOccasions />
        <HomeBrandSlider />
        <S.Categories>
          <S.HeaderWrapper>
            <Typography variant="subtitle1Bold">{t('popularItems')}</Typography>
            <VerticalDivider
              $margin="0 16px"
              $height="24px"
              color="var(--color-components-input-border-strong)"
            />
            <Button
              size="lg"
              variant="link"
              color="bg"
              onClick={() => navigate(ROUTE_PATHS.GIFT_CATALOG)}
            >
              {t('seeAll')}
            </Button>
          </S.HeaderWrapper>
          <S.ProductCardsContainer>
            {productsData?.docs?.map(product => {
              const { merchantProductId } = product || {};
              const isSelected =
                products?.filter(item => item.merchantProductId === merchantProductId)?.length > 0;

              return (
                <S.ProductCardWrapper
                  key={merchantProductId}
                  style={{ width: constants.products?.card?.width }}
                >
                  <ProductCard
                    product={product}
                    isSelected={isSelected}
                    isDraft
                    onClick={() => handleShowProductModal({ product, isSelected })}
                  />
                </S.ProductCardWrapper>
              );
            })}
          </S.ProductCardsContainer>
        </S.Categories>
      </S.Content>

      {!!productDetail && (
        <ProductListItemDetail
          isOpen={!!productDetail}
          onRequestClose={handleHideProductModal}
          okText={t('includeThisGift')}
          product={productDetail.product}
          isSelectedProduct={productDetail?.isSelected}
          initialSelectedSkus={productDetail?.productWithSkuSelection?.skus}
          onOk={onSelectProduct}
          featureType="gift-box"
          isDraft
          onUpdateProduct={product => {
            updateProducts(
              products?.map(item => {
                if (item.merchantProductId === product.merchantProductId) {
                  return product;
                }
                return item;
              })
            );
          }}
        />
      )}
    </S.HomeTemplate>
  );
}

const HomeTemplateComponent = withMerchantComponents({
  Component: HomeTemplate,
  fileName: 'HomeTemplateComponent',
  atomicFolderName: 'templates'
});

export { HomeTemplateComponent };
